import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import SearchInput from "@/components/SearchInput";
import { Button } from "@/components/ui/button";
import { SellerBalance } from "@/components/SellerBalance";
import InsuranceBadge from "@/components/InsuranceBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { LrnChipSearch } from "@/components/ui/lrn-chip-search";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { PageHeader, DataTable, StatusBadge, EmptyState } from "@/components/ui-system";
import { toast } from "sonner";
import { Edit, Package, Plus, Calendar, SlidersHorizontal, X, Search } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import AppointmentWarning from "@/components/AppointmentWarning";
import { TIME_RANGES, getMinAppointmentDate, getMaxAppointmentDate, isDelhiveryAppointmentBooked, validateReferenceRequiredForAppointment, validatePoExpiryAndAppointment, validateOrderPoExpiry } from "@/utils/appointmentUtils";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";
import FreightAuditDialog from "@/components/admin/FreightAuditDialog";

// Confirmation methods for an EdgeLMS-only (manual) appointment — same as the Appointments page.
const MANUAL_METHODS = [
  { value: "green_channel", label: "Updated Via Green Channel" },
  { value: "partner_email", label: "Updated Via Partner confirmed on Email" },
  { value: "partner_whatsapp", label: "Updated Via Partner confirmed on Whatsapp" },
  { value: "partner_sms", label: "Updated Via Partner confirmed on SMS" },
  { value: "other", label: "Other" },
];

const ORDER_STATUSES = [
  "CREATED", "MANIFESTED", "PICKED_UP", "LEFT_ORIGIN", "REACH_DESTINATION",
  "UNDEL_REATTEMPT", "PART_DEL", "OFD", "DELIVERED", "RETURNED_INTRANSIT",
  "RECEIVED_AT_RETURN_CENTER", "RETURN_OFD", "RETURN_DELIVERED", "NOT_PICKED", "LOST"
];

const TABS = [
  { id: 'new', label: 'New Orders', statuses: ['CREATED', 'MANIFESTED'] },
  { id: 'in_process', label: 'In-Process', statuses: ['ASSIGNED', 'PICKED_UP', 'LEFT_ORIGIN', 'REACH_DESTINATION', 'UNDEL_REATTEMPT', 'PART_DEL', 'OFD'] },
  { id: 'delivered', label: 'Delivered', statuses: ['DELIVERED'] },
  { id: 'returned', label: 'Returned', statuses: ['RETURNED', 'RETURNED_INTRANSIT', 'RECEIVED_AT_RETURN_CENTER', 'RETURN_OFD', 'RETURN_DELIVERED'] },
  { id: 'lost', label: 'LOST', statuses: ['LOST'] },
  { id: 'cancelled', label: 'Cancelled', statuses: ['CANCELLED'] },
  { id: 'not_picked', label: 'Not Picked', statuses: ['NOT_PICKED'] },
  { id: 'all', label: 'All', statuses: [] },
];

const ITEMS_PER_PAGE = 50;

// Statuses that allow appointment editing
// Do NOT allow appointment actions for final statuses: DELIVERED, RETURN_DELIVERED, LOST.
// REACH_DESTINATION is included so a BREACHED appointment (reached the hub, delivery slot missed)
// can still be re-booked to a new date from the Orders list.
const APPOINTMENT_EDITABLE_STATUSES = ['CREATED', 'APPOINTMENT_SET', 'MANIFESTED', 'PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_PICKUP', 'OFD', 'REACH_DESTINATION'];

// Final statuses where appointments should not be shown
const FINAL_STATUSES = ['DELIVERED', 'RETURN_DELIVERED', 'LOST', 'RETURN_OFD', 'CANCELLED'];

// The order's reference list (new array, else legacy single field). A "complete" reference has
// BOTH a number and a date — a legacy ref-without-date must be re-collected, not silently reused.
const refListOf = (o) => (o?.reference_numbers?.length
  ? o.reference_numbers
  : (o?.reference_number ? [{ reference_number: o.reference_number, date: o.reference_number_date || "" }] : []));
const hasCompleteRef = (o) => refListOf(o).some((r) => (r.reference_number || "").trim() && (r.date || "").trim());

// Advanced-filter option lists. payment_mode / insurance_option are exact enums from
// order creation; freight_mode / pickup_type values are the known set (verify vs data).
const PAYMENT_MODES = [{ v: 'Prepaid', l: 'Prepaid' }, { v: 'COD', l: 'COD' }];
const INSURANCE_OPTIONS = [{ v: 'owner_risk', l: 'Owner Risk' }, { v: 'carrier_insurance', l: 'Carrier Insurance' }];
const PICKUP_TYPES = [{ v: 'fm_pickup', l: 'First-Mile Pickup' }, { v: 'self_drop', l: 'Self Drop' }];
const FREIGHT_MODES = [{ v: 'BTC', l: 'BTC' }, { v: 'TBB', l: 'TBB' }, { v: 'TBP', l: 'TBP' }];
const YES_NO = [{ v: 'yes', l: 'Yes' }, { v: 'no', l: 'No' }];
const EWAY_OPTS = [{ v: 'present', l: 'Present' }, { v: 'absent', l: 'Absent' }];
const ZONE_OPTS = ['N1', 'N2', 'E', 'NE', 'W1', 'W2', 'S1', 'S2', 'Central'].map((z) => ({ v: z, l: z }));

const FilterSelect = ({ label, value, onChange, placeholder, options }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-slate-600">{label}</Label>
    <SearchableSelect
      value={value}
      onValueChange={onChange}
      placeholder={placeholder}
      options={options.map((o) => ({ value: o.v, label: o.l }))}
    />
  </div>
);

const DateRangeField = ({ label, s, e, onS, onE }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-slate-600">{label}</Label>
    <div className="grid grid-cols-2 gap-2">
      <Input type="date" value={s} onChange={(ev) => onS(ev.target.value)} />
      <Input type="date" value={e} onChange={(ev) => onE(ev.target.value)} />
    </div>
  </div>
);

const NumRangeField = ({ label, lo, hi, onLo, onHi }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-slate-600">{label}</Label>
    <div className="grid grid-cols-2 gap-2">
      <Input type="number" min="0" placeholder="Min" value={lo} onChange={(ev) => onLo(ev.target.value)} />
      <Input type="number" min="0" placeholder="Max" value={hi} onChange={(ev) => onHi(ev.target.value)} />
    </div>
  </div>
);

const AdminOrders = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [freightAuditOpen, setFreightAuditOpen] = useState(false);  // bulk freight discrepancy audit
  const [orders, setOrders] = useState([]);          // current page of orders (server-paginated)
  const [tabCounts, setTabCounts] = useState({});    // { new, in_process, ..., all }
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showLRNDialog, setShowLRNDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [lrnValue, setLrnValue] = useState("");
  const [assignmentMode, setAssignmentMode] = useState("manual"); // manual or auto
  const [shippingPartnerId, setShippingPartnerId] = useState("");
  const [actualShippingPartner, setActualShippingPartner] = useState("");
  const [shippingPartners, setShippingPartners] = useState([]);
  const [statusValue, setStatusValue] = useState("");
  const [statusUpdateMode, setStatusUpdateMode] = useState(""); // "" (mode selection), "auto", or "manual"
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTimeRange, setAppointmentTimeRange] = useState("");
  // Inline reference/PO capture when setting an appointment on an order that has none yet
  // (appointment => reference => date + PO expiry).
  const [apptRefNumber, setApptRefNumber] = useState("");
  const [apptRefDate, setApptRefDate] = useState("");
  const [apptPoExpiry, setApptPoExpiry] = useState("");
  const [apptDelhiveryId, setApptDelhiveryId] = useState("");   // Delhivery appointment ID (required)
  // Add-Appointment mode: "partner" books with the carrier; "manual" records it in EdgeLMS
  // only (Green Channel / partner-confirmed) with no carrier call — mirrors the Appointments page.
  const [apptMode, setApptMode] = useState("partner");
  const [manualMethod, setManualMethod] = useState("");
  const [manualMethodOther, setManualMethodOther] = useState("");
  const [manualComment, setManualComment] = useState("");
  const [activeTab, setActiveTab] = useLocalStorageState('admin-orders:tab', 'new');
  const [currentPage, setCurrentPage] = useState(1);

  // Search and Filter states (persisted across reloads/navigation)
  // Multi-value LRN/reference search: a set of chips; the backend gets them comma-joined.
  const [searchChips, setSearchChips] = useLocalStorageState('admin-orders:search-chips', []);
  const [selectedMarketplace, setSelectedMarketplace] = useLocalStorageState('admin-orders:marketplace', "");
  const [selectedSeller, setSelectedSeller] = useLocalStorageState('admin-orders:seller', "");
  const [selectedStatus, setSelectedStatus] = useLocalStorageState('admin-orders:status', "");
  const [startDate, setStartDate] = useLocalStorageState('admin-orders:startDate', "");
  const [endDate, setEndDate] = useLocalStorageState('admin-orders:endDate', "");
  const [marketplaces, setMarketplaces] = useState([]);
  const [sellers, setSellers] = useState([]);

  // Advanced filters (persisted as one object) + the left slide-out panel.
  const [adv, setAdv] = useLocalStorageState('admin-orders:adv', {});
  const [filterOpen, setFilterOpen] = useState(false);
  const setAdvField = (key, value) => setAdv((prev) => ({ ...(prev || {}), [key]: value }));
  const advKey = JSON.stringify(adv || {});
  const searchKey = (searchChips || []).join(",");   // stable dep + the value sent to the server

  useEffect(() => {
    fetchMarketplaces();
    fetchSellers();
    fetchShippingPartners();
  }, []);

  // Any tab/filter change resets to page 1 (a page change alone does not).
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchKey, selectedMarketplace, selectedSeller, selectedStatus, startDate, endDate, advKey]);

  // Server-side fetch: current tab + filters + page, plus tab counts.
  useEffect(() => {
    fetchOrders();
  }, [activeTab, currentPage, searchKey, selectedMarketplace, selectedSeller, selectedStatus, startDate, endDate, advKey]);

  const buildOrderParams = (includeTabAndPage) => {
    const p = {};
    if (searchKey) p.search = searchKey;
    if (selectedMarketplace && selectedMarketplace !== "all") p.marketplace_id = selectedMarketplace;
    if (selectedSeller && selectedSeller !== "all") p.seller_id = selectedSeller;
    if (selectedStatus && selectedStatus !== "all") p.status = selectedStatus;
    if (startDate) p.start_date = startDate;
    if (endDate) p.end_date = endDate;
    // Advanced filters (sidebar): only non-empty, non-"all" values are sent.
    Object.entries(adv || {}).forEach(([k, v]) => {
      if (v !== "" && v != null && v !== "all") p[k] = v;
    });
    if (includeTabAndPage) {
      p.tab = activeTab;
      p.page = currentPage;
      p.limit = ITEMS_PER_PAGE;
    }
    return p;
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const [listRes, countsRes] = await Promise.all([
        axios.get(`${API}/admin/orders/list`, { params: buildOrderParams(true) }),
        axios.get(`${API}/admin/orders/tab-counts`, { params: buildOrderParams(false) }),
      ]);
      setOrders(listRes.data.orders || []);
      setTotal(listRes.data.total || 0);
      setTotalPages(listRes.data.total_pages || 1);
      setTabCounts(countsRes.data || {});
    } catch (error) {
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketplaces = async () => {
    try {
      const response = await axios.get(`${API}/b2b-marketplace/`);
      setMarketplaces(response.data);
    } catch (error) {
      console.error("Failed to fetch marketplaces");
    }
  };

  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API}/sellers-org/`);
      setSellers(response.data);
    } catch (error) {
      console.error("Failed to fetch sellers");
    }
  };

  const fetchShippingPartners = async () => {
    try {
      const response = await axios.get(`${API}/b2b-shipping-partner/`);
      setShippingPartners(response.data.filter(sp => sp.is_active));
    } catch (error) {
      console.error("Failed to fetch shipping partners");
    }
  };

  // Server already returns the current page and total pages/counts — these just surface them.
  const getPaginatedOrders = () => orders;
  const getTotalPages = () => totalPages;

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchChips([]);
    setSelectedMarketplace("");
    setSelectedSeller("");
    setSelectedStatus("");
    setStartDate("");
    setEndDate("");
    setAdv({});
  };

  const getTabCount = (tabId) => tabCounts[tabId] ?? 0;

  const handleDownloadDocument = async (orderId, docType) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/download/${docType}`);
      window.open(response.data.download_url, '_blank');
    } catch (error) {
      toast.error("Failed to generate download link");
    }
  };

  const openLRNDialog = (order) => {
    setSelectedOrder(order);
    setAssignmentMode("manual");
    setLrnValue(order.lrn || "");
    setShippingPartnerId(order.shipping_partner_id || "");
    setActualShippingPartner(order.actual_shipping_partner || "");
    setShowLRNDialog(true);
  };

  const openStatusDialog = (order) => {
    setSelectedOrder(order);
    setStatusValue(order.status);
    setStatusUpdateMode(""); // Reset to mode selection
    setShowStatusDialog(true);
  };

  const handleAssignLRN = async () => {
    if (assignmentMode === "manual") {
      // Manual assignment
      if (!lrnValue.trim()) {
        toast.error("LRN cannot be empty");
        return;
      }

      if (!shippingPartnerId) {
        toast.error("Shipping partner is required");
        return;
      }

      const selectedPartner = shippingPartners.find(p => p.id === shippingPartnerId);
      if (selectedPartner?.is_aggregator && !actualShippingPartner.trim()) {
        toast.error("Actual shipping partner is required for aggregators");
        return;
      }

      try {
        await axios.put(`${API}/orders/${selectedOrder.id}/lrn`, { 
          lrn: lrnValue,
          shipping_partner_id: shippingPartnerId,
          actual_shipping_partner: actualShippingPartner || null
        });
        toast.success("LRN assigned successfully!");
        setShowLRNDialog(false);
        setLrnValue("");
        setShippingPartnerId("");
        setActualShippingPartner("");
        setAssignmentMode("manual");
        fetchOrders();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to assign LRN");
      }
    } else {
      // Auto assignment
      if (!shippingPartnerId) {
        toast.error("Please select a shipping partner");
        return;
      }

      try {
        toast.loading("Assigning LRN automatically...");
        const response = await axios.post(`${API}/orders/${selectedOrder.id}/auto-assign-lrn`, {
          shipping_partner_id: shippingPartnerId
        });
        toast.dismiss();
        if (response.data.status === "PROCESSING") {
          toast.success(response.data.message, { duration: 6000 });
        } else {
          toast.success(`LRN assigned successfully! LRN: ${response.data.lrn}`);
        }
        setShowLRNDialog(false);
        setLrnValue("");
        setShippingPartnerId("");
        setActualShippingPartner("");
        setAssignmentMode("manual");
        fetchOrders();
      } catch (error) {
        toast.dismiss();
        toast.error(error.response?.data?.detail || "Auto-assignment failed");
      }
    }
  };

  const handleAutoFetchStatus = async () => {
    try {
      toast.loading("Fetching latest status from shipping partner...");
      
      // Call the tracking API for this specific order
      const response = await axios.post(`${API}/orders/${selectedOrder.id}/track-status`);
      
      toast.dismiss();
      toast.success(response.data.message || "Status updated successfully!");
      setShowStatusDialog(false);
      setStatusUpdateMode("");
      fetchOrders();
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.detail || "Failed to fetch status from shipping partner");
    }
  };

  const handleUpdateStatus = async () => {
    if (statusUpdateMode === "auto") {
      await handleAutoFetchStatus();
    } else if (statusUpdateMode === "manual") {
      try {
        await axios.put(`${API}/orders/${selectedOrder.id}/status`, { status: statusValue });
        toast.success("Status updated successfully!");
        setShowStatusDialog(false);
        setStatusUpdateMode("");
        fetchOrders();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to update status");
      }
    }
  };

  const handleOpenAppointmentDialog = (order) => {
    setSelectedOrder(order);
    // If order has appointment, pre-fill for editing
    if (order.appointment_date && order.appointment_time_range) {
      setAppointmentDate(order.appointment_date);
      setAppointmentTimeRange(order.appointment_time_range);
    } else if (order.appointment_datetime) {
      // Backward compatibility - convert old format
      const date = new Date(order.appointment_datetime);
      setAppointmentDate(date.toISOString().split('T')[0]);
      setAppointmentTimeRange("9:00 AM - 12:00 PM");
    } else {
      setAppointmentDate("");
      setAppointmentTimeRange("");
    }
    const firstRef = refListOf(order)[0] || {};
    setApptRefNumber(firstRef.reference_number || "");
    setApptRefDate(firstRef.date || "");
    setApptPoExpiry(order.po_expiry_date ? String(order.po_expiry_date).slice(0, 10) : "");
    setApptDelhiveryId(order.delhivery_appointment_id || order.appointment_id || "");
    setApptMode("partner");
    setManualMethod("");
    setManualMethodOther("");
    setManualComment("");
    setShowAppointmentDialog(true);
  };

  const handleUpdateAppointment = async () => {
    if (!appointmentDate || !appointmentTimeRange) {
      toast.error("Please select appointment date and time range");
      return;
    }
    const manual = apptMode === "manual";
    // The Delhivery Appointment ID is only needed when we actually book with the carrier.
    const apptIdRequired = !manual && !!marketplaces.find((m) => m.id === selectedOrder?.b2b_marketplace_id)?.appointment_id_required;
    if (apptIdRequired && !apptDelhiveryId.trim()) {
      toast.error("Appointment ID is required to book an appointment for this marketplace.");
      return;
    }
    if (manual) {
      if (!manualMethod) { toast.error("Select how the appointment was confirmed with the partner"); return; }
      if (manualMethod === "other" && !manualMethodOther.trim()) {
        toast.error("Please describe the 'Other' confirmation method"); return;
      }
    }

    // Setting an appointment requires a COMPLETE reference/PO number (number + date) and the order
    // PO expiry. If the order lacks a complete one, the dialog collects them inline and we send them.
    const orderHasCompleteRef = hasCompleteRef(selectedOrder);
    const orderHasPoExpiry = !!selectedOrder?.po_expiry_date;
    const collectedRef = !orderHasCompleteRef;
    const showPoExpiryField = !orderHasCompleteRef || !orderHasPoExpiry;
    const effRefs = collectedRef
      ? [{ reference_number: (apptRefNumber || "").trim(), date: (apptRefDate || "").trim() }].filter((r) => r.reference_number)
      : refListOf(selectedOrder);
    const effPoExpiry = showPoExpiryField ? (apptPoExpiry || "") : (selectedOrder?.po_expiry_date ? String(selectedOrder.po_expiry_date).slice(0, 10) : "");

    const refErr = validateReferenceRequiredForAppointment(effRefs, appointmentDate)
      || validatePoExpiryAndAppointment(effRefs, appointmentDate)
      || validateOrderPoExpiry(effRefs, effPoExpiry, appointmentDate);
    if (refErr) {
      toast.error(refErr);
      return;
    }

    try {
      const body = { appointment_date: appointmentDate, appointment_time_range: appointmentTimeRange, appointment_id: apptDelhiveryId.trim() };
      if (collectedRef) body.reference_numbers = effRefs;
      if (showPoExpiryField) body.po_expiry_date = effPoExpiry || null;
      if (manual) {
        // EdgeLMS-only: records the appointment + how it was confirmed; no carrier call.
        body.update_method = manualMethod;
        body.update_method_other = manualMethodOther.trim();
        body.comment = manualComment.trim();
        await axios.post(
          `${API}/appointments/orders/${selectedOrder.id}/appointment/manual`,
          body,
          { headers: { 'Content-Type': 'application/json' } }
        );
        toast.success("Appointment recorded in EdgeLMS only (partner not called).");
      } else {
        const resp = await axios.put(
          `${API}/appointments/orders/${selectedOrder.id}/appointment`,
          body,
          { headers: { 'Content-Type': 'application/json' } }
        );
        const booking = resp.data?.delhivery_booking;
        if (booking?.success) {
          toast.success("Appointment saved and booked with Delhivery!");
        } else if (booking && !booking.skipped && booking.error) {
          toast.warning("Appointment saved, but Delhivery booking failed.");
        } else {
          toast.success("Appointment updated successfully!");
        }
      }
      setShowAppointmentDialog(false);
      setAppointmentDate("");
      setAppointmentTimeRange("");
      setApptDelhiveryId("");
      fetchOrders();
    } catch (error) {
      console.error("Appointment update error:", error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          "Failed to update appointment";
      toast.error(typeof errorMessage === 'string' ? errorMessage : "Failed to update appointment");
    }
  };

  const showEdd = !['delivered', 'returned', 'lost', 'cancelled', 'not_picked'].includes(activeTab);

  const columns = [
    { key: 'lrn', header: 'LRN', cell: (order) => (
        <div className="flex flex-col items-start gap-1">
          {order.lrn ? (
            <button onClick={() => navigate(`/admin/orders/${order.order_id}`)} className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline">{order.lrn}</button>
          ) : order.status === 'CANCELLED' ? (
            <span className="text-sm text-slate-400">—</span>
          ) : (
            <Button size="sm" variant="outline" onClick={() => openLRNDialog(order)} className="text-blue-600">Assign LRN</Button>
          )}
          <InsuranceBadge value={order.insurance_option} />
        </div>
      ) },
    { key: 'sp', header: 'Shipping Partner', cell: (order) => order.shipping_partner_name ? (
        <div className="text-sm"><span className="font-medium text-slate-900">{order.shipping_partner_name}</span>{order.actual_shipping_partner && <div className="text-xs text-slate-500">via {order.actual_shipping_partner}</div>}</div>
      ) : <span className="text-sm text-slate-400">-</span> },
    { key: 'zone', header: 'Zone', cell: (order) => (order.origin_zone || order.dest_zone) ? (
        <span className="text-sm text-slate-700 whitespace-nowrap">{order.origin_zone || '—'} <span className="text-slate-400">→</span> {order.dest_zone || '—'}</span>
      ) : <span className="text-sm text-slate-400">-</span> },
    { key: 'order_no', header: 'Order ID', cell: (order) => order.order_number ? (
        <button onClick={() => navigate(`/admin/orders/${order.order_id}`)} className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap">{order.order_number}</button>
      ) : <span className="text-sm text-slate-400">-</span> },
    { key: 'ref', header: 'Reference No.', primary: true, cell: (order) => (
        <button onClick={() => navigate(`/admin/orders/${order.order_id}`)} className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline">{order.InfoFromPartnerLRCopy?.shippers_reference_no || order.reference_number || '-'}</button>
      ) },
    { key: 'mp', header: 'Marketplace', cell: (order) => order.b2b_marketplace_name ? (
        <div className="flex items-center gap-2 justify-end md:justify-start">{order.b2b_marketplace_logo && <img src={order.b2b_marketplace_logo} alt={order.b2b_marketplace_name} className="h-6 w-6 object-contain" />}<span className="text-sm text-slate-600">{order.b2b_marketplace_name}</span></div>
      ) : <span className="text-sm text-slate-600">-</span> },
    { key: 'seller', header: 'Seller', cell: (order) => (
      <div>
        <div className="text-sm text-slate-700">{order.seller_brand_name || order.seller_id}</div>
        <SellerBalance wallet={order.seller_wallet} />
      </div>
    ) },
    { key: 'inv', header: 'Invoice No.', cell: (order) => <span className="text-sm text-slate-700">{order.InfoFromPartnerLRCopy?.invoice_no || order.invoice_number}</span> },
    { key: 'boxes', header: 'Boxes', align: 'right', cell: (order) => <span className="text-sm text-slate-700">{order.InfoFromPartnerLRCopy?.total_number_of_boxes || order.number_of_boxes}</span> },
    { key: 'amount', header: 'Amount', align: 'right', cell: (order) => <span className="text-sm font-medium text-slate-900">₹{(order.InfoFromPartnerLRCopy?.total_invoice_value ? parseFloat(order.InfoFromPartnerLRCopy.total_invoice_value) : (order.total_invoice_amount || order.invoice_amount || 0)).toLocaleString('en-IN')}</span> },
    { key: 'appt', header: 'Appointment', cell: (order) => (order.appointment_date || order.appointment_datetime) ? (
        <div className="flex items-center gap-3 justify-end md:justify-start">
          <div className="whitespace-nowrap text-sm text-slate-700">
            {order.appointment_date ? (
              <>
                <div>{order.appointment_date}</div>
                {order.appointment_time_range && <div className="text-xs text-slate-500">{order.appointment_time_range}</div>}
              </>
            ) : (
              <>
                <div>{formatToIST(order.appointment_datetime, false)}</div>
                <div className="text-xs text-slate-500">{formatToIST(order.appointment_datetime).split(', ')[1]}</div>
              </>
            )}
            {isDelhiveryAppointmentBooked(order) && (
              <span className="inline-block mt-1 px-1.5 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium" title={order.delhivery_appointment_id ? `Booked with Delhivery — Appointment ID: ${order.delhivery_appointment_id}` : "Booked with Delhivery"}>Booked ✓</span>
            )}
          </div>
          {APPOINTMENT_EDITABLE_STATUSES.includes(order.status) && (
            <button onClick={() => handleOpenAppointmentDialog(order)} className="text-slate-900 hover:text-teal-600 transition-colors p-1.5 rounded hover:bg-teal-50 flex-shrink-0" title="Edit appointment"><Edit className="w-5 h-5" /></button>
          )}
        </div>
      ) : (!FINAL_STATUSES.includes(order.status) && (
        <Button size="sm" variant="outline" onClick={() => handleOpenAppointmentDialog(order)} className="text-xs"><Calendar className="w-3 h-3 mr-1" />Add</Button>
      )) },
    { key: 'created', header: 'Created', cell: (order) => (
        <div className="whitespace-nowrap text-sm text-slate-700">
          <div>{formatToIST(order.created_at, false)}</div>
          <div className="text-xs text-slate-500">{formatToIST(order.created_at).split(', ')[1]}</div>
        </div>
      ) },
    ...(showEdd ? [{ key: 'edd', header: 'EDD', cell: (order) => order.estimated_dates && order.estimated_dates.length > 0 ? (
        <span className="text-sm text-blue-600 font-medium whitespace-nowrap">{(() => { const l = order.estimated_dates[order.estimated_dates.length - 1]; const d = l?.date || l; if (!d) return '-'; try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } })()}</span>
      ) : <span className="text-sm text-slate-400">-</span> }] : []),
    { key: 'status', header: 'Status', cell: (order) => <StatusBadge status={order.status} kind="order" /> },
    { key: 'actions', header: 'Actions', isActions: true, cell: (order) => !['DELIVERED', 'RETURN_DELIVERED'].includes(order.status) ? (
        <Button size="sm" variant="outline" onClick={() => openStatusDialog(order)}>
          <Edit className="w-3 h-3 md:mr-0 mr-2" /><span className="md:hidden">Update Status</span>
        </Button>
      ) : null },
  ];

  // Active-filter chips (base + advanced) for the summary strip and the count badge.
  const ADV_LABELS = {
    shipping_partner_id: 'Partner', payment_mode: 'Payment', freight_mode: 'Freight',
    pickup_type: 'Pickup', insurance_option: 'Insurance', lrn_assigned: 'LRN', eway_bill: 'E-way',
    origin_zone: 'Origin Zone', dest_zone: 'Dest Zone',
    boxes_min: 'Boxes ≥', boxes_max: 'Boxes ≤', amount_min: 'Amount ≥', amount_max: 'Amount ≤',
    appt_start: 'Appt ≥', appt_end: 'Appt ≤', manifested_start: 'Manifested ≥',
    manifested_end: 'Manifested ≤', delivered_start: 'Delivered ≥', delivered_end: 'Delivered ≤',
  };
  const activeChips = [
    (selectedMarketplace && selectedMarketplace !== 'all') && { k: 'mp', label: `Marketplace: ${marketplaces.find(m => m.id === selectedMarketplace)?.name || selectedMarketplace}`, clear: () => setSelectedMarketplace('') },
    (selectedSeller && selectedSeller !== 'all') && { k: 'seller', label: `Seller: ${sellers.find(s => s.seller_id === selectedSeller)?.brand_name || sellers.find(s => s.seller_id === selectedSeller)?.company_registered_name || selectedSeller}`, clear: () => setSelectedSeller('') },
    (selectedStatus && selectedStatus !== 'all') && { k: 'status', label: `Status: ${selectedStatus.replace(/_/g, ' ')}`, clear: () => setSelectedStatus('') },
    startDate && { k: 'sd', label: `Created ≥ ${startDate}`, clear: () => setStartDate('') },
    endDate && { k: 'ed', label: `Created ≤ ${endDate}`, clear: () => setEndDate('') },
    ...Object.entries(adv || {})
      .filter(([, v]) => v !== '' && v != null && v !== 'all')
      .map(([k, v]) => ({
        k,
        label: `${ADV_LABELS[k] || k}: ${k === 'shipping_partner_id' ? (shippingPartners.find(p => p.id === v)?.name || v) : v}`,
        clear: () => setAdvField(k, ''),
      })),
  ].filter(Boolean);
  const activeFilterCount = activeChips.length;

  const emptyNode = (
    <EmptyState
      icon={Package}
      title={orders.length === 0 ? "No orders yet" : "No orders in this category"}
      description={orders.length === 0 ? "Orders will appear here once sellers create them" : "Try selecting a different tab"}
    />
  );

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader
          title="Orders"
          subtitle="Manage all delivery orders"
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={() => setFreightAuditOpen(true)} data-testid="open-freight-audit">
                <Search className="w-4 h-4 mr-2" />
                Check Freight Charges
              </Button>
              <Button onClick={() => navigate('/admin/orders/create')}>
                <Plus className="w-4 h-4 mr-2" />
                Create Order
              </Button>
            </div>
          }
        />

        <FreightAuditDialog open={freightAuditOpen} onOpenChange={setFreightAuditOpen} />

        <AppointmentWarning />

        {/* Tabs — wrap onto multiple rows so all are visible without horizontal scrolling */}
        <div className="flex flex-wrap gap-x-1 gap-y-0 border-b border-slate-200 bg-white px-2 sm:px-4 rounded-t-lg shadow-lg">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-3 sm:px-4 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-brand text-brand'
                  : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
              }`}
            >
              {tab.label} <span className="font-bold text-brand">({getTabCount(tab.id)})</span>
            </button>
          ))}
        </div>

        {/* Search + Filters trigger + active-filter chips */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6 space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <LrnChipSearch chips={searchChips || []} onChange={setSearchChips} maxChips={25} />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setFilterOpen(true)} className="whitespace-nowrap">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center rounded-full bg-brand text-white text-xs font-semibold h-5 min-w-[1.25rem] px-1.5">{activeFilterCount}</span>
                  )}
                </Button>
                {activeFilterCount > 0 && (
                  <Button variant="ghost" onClick={handleClearFilters} className="whitespace-nowrap text-slate-600">Clear all</Button>
                )}
              </div>
            </div>
            {activeChips.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {activeChips.map((chip) => (
                  <span key={chip.k} className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 text-xs px-2.5 py-1">
                    {chip.label}
                    <button onClick={chip.clear} className="text-slate-400 hover:text-slate-700" aria-label="Remove filter">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right slide-out filter panel — all filters grouped */}
        <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
          <SheetContent side="right" className="w-[92vw] sm:max-w-md overflow-y-auto flex flex-col">
            <SheetHeader>
              <SheetTitle>Filters</SheetTitle>
            </SheetHeader>
            <div className="flex-1 space-y-6 py-4">
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Basics</p>
                <FilterSelect label="Marketplace" value={selectedMarketplace} onChange={setSelectedMarketplace} placeholder="All Marketplaces"
                  options={[{ v: 'all', l: 'All Marketplaces' }, ...marketplaces.map((m) => ({ v: m.id, l: m.name }))]} />
                <FilterSelect label="Seller" value={selectedSeller} onChange={setSelectedSeller} placeholder="All Sellers"
                  options={[{ v: 'all', l: 'All Sellers' }, ...sellers.map((s) => ({ v: s.seller_id, l: s.brand_name || s.company_registered_name }))]} />
                <FilterSelect label="Status" value={selectedStatus} onChange={setSelectedStatus} placeholder="All Statuses"
                  options={[{ v: 'all', l: 'All Statuses' }, ...ORDER_STATUSES.map((s) => ({ v: s, l: s.replace(/_/g, ' ') }))]} />
              </div>
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Dates</p>
                <DateRangeField label="Created" s={startDate} e={endDate} onS={setStartDate} onE={setEndDate} />
                <DateRangeField label="Appointment" s={adv.appt_start || ''} e={adv.appt_end || ''} onS={(v) => setAdvField('appt_start', v)} onE={(v) => setAdvField('appt_end', v)} />
                <DateRangeField label="Manifested" s={adv.manifested_start || ''} e={adv.manifested_end || ''} onS={(v) => setAdvField('manifested_start', v)} onE={(v) => setAdvField('manifested_end', v)} />
                <DateRangeField label="Delivered" s={adv.delivered_start || ''} e={adv.delivered_end || ''} onS={(v) => setAdvField('delivered_start', v)} onE={(v) => setAdvField('delivered_end', v)} />
              </div>
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Shipment</p>
                <FilterSelect label="Payment Mode" value={adv.payment_mode || ''} onChange={(v) => setAdvField('payment_mode', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...PAYMENT_MODES]} />
                <FilterSelect label="Shipping Partner" value={adv.shipping_partner_id || ''} onChange={(v) => setAdvField('shipping_partner_id', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...shippingPartners.map((p) => ({ v: p.id, l: p.name }))]} />
                <FilterSelect label="Freight Mode" value={adv.freight_mode || ''} onChange={(v) => setAdvField('freight_mode', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...FREIGHT_MODES]} />
                <FilterSelect label="Pickup Type" value={adv.pickup_type || ''} onChange={(v) => setAdvField('pickup_type', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...PICKUP_TYPES]} />
                <FilterSelect label="Insurance" value={adv.insurance_option || ''} onChange={(v) => setAdvField('insurance_option', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...INSURANCE_OPTIONS]} />
                <FilterSelect label="Origin Zone" value={adv.origin_zone || ''} onChange={(v) => setAdvField('origin_zone', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...ZONE_OPTS]} />
                <FilterSelect label="Dest Zone" value={adv.dest_zone || ''} onChange={(v) => setAdvField('dest_zone', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...ZONE_OPTS]} />
                <FilterSelect label="LRN Assigned" value={adv.lrn_assigned || ''} onChange={(v) => setAdvField('lrn_assigned', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...YES_NO]} />
                <FilterSelect label="E-way Bill" value={adv.eway_bill || ''} onChange={(v) => setAdvField('eway_bill', v)} placeholder="Any" options={[{ v: 'all', l: 'Any' }, ...EWAY_OPTS]} />
              </div>
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Quantities &amp; Amounts</p>
                <NumRangeField label="Boxes" lo={adv.boxes_min || ''} hi={adv.boxes_max || ''} onLo={(v) => setAdvField('boxes_min', v)} onHi={(v) => setAdvField('boxes_max', v)} />
                <NumRangeField label="Invoice Amount (₹)" lo={adv.amount_min || ''} hi={adv.amount_max || ''} onLo={(v) => setAdvField('amount_min', v)} onHi={(v) => setAdvField('amount_max', v)} />
              </div>
            </div>
            <SheetFooter className="flex-row gap-2">
              <Button variant="outline" onClick={handleClearFilters} className="flex-1">Clear all</Button>
              <Button onClick={() => setFilterOpen(false)} className="flex-1" style={{ backgroundColor: '#00A699' }}>Done</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        <DataTable
          columns={columns}
          data={getPaginatedOrders()}
          rowKey={(o) => o.order_id}
          loading={loading}
          empty={emptyNode}
          pagination={getTotalPages() > 1 ? {
            page: currentPage,
            pages: getTotalPages(),
            total: total,
            pageSize: ITEMS_PER_PAGE,
            onPageChange: setCurrentPage,
          } : undefined}
        />
      </div>

      {/* LRN Assignment Dialog */}
      <Dialog open={showLRNDialog} onOpenChange={setShowLRNDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign LRN</DialogTitle>
            <DialogDescription>
              Assign a Logistics Reference Number to this order
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Assignment Mode Selection */}
            <div className="space-y-2">
              <Label>Assignment Mode *</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="assignmentMode"
                    value="manual"
                    checked={assignmentMode === "manual"}
                    onChange={(e) => setAssignmentMode(e.target.value)}
                    className="w-4 h-4 text-[#00A699] focus:ring-[#00A699]"
                  />
                  <span className="text-sm font-medium">Manual</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="assignmentMode"
                    value="auto"
                    checked={assignmentMode === "auto"}
                    onChange={(e) => setAssignmentMode(e.target.value)}
                    className="w-4 h-4 text-[#00A699] focus:ring-[#00A699]"
                  />
                  <span className="text-sm font-medium">Auto</span>
                </label>
              </div>
              <p className="text-xs text-slate-500">
                {assignmentMode === "manual" 
                  ? "Manually enter LR number and select shipping partner" 
                  : "Automatically generate LR number using shipping partner API"}
              </p>
            </div>

            {assignmentMode === "manual" ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="lrn">LRN *</Label>
                  <Input
                    id="lrn"
                    value={lrnValue}
                    onChange={(e) => setLrnValue(e.target.value)}
                    placeholder="Enter LRN"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shipping_partner">Shipping Partner *</Label>
                  <Select
                    value={shippingPartnerId}
                    onValueChange={(value) => {
                      setShippingPartnerId(value);
                      setActualShippingPartner("");
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select shipping partner" />
                    </SelectTrigger>
                    <SelectContent>
                      {shippingPartners.map((partner) => (
                        <SelectItem key={partner.id} value={partner.id}>
                          {partner.name} {partner.is_aggregator && "(Aggregator)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {shippingPartnerId && shippingPartners.find(p => p.id === shippingPartnerId)?.is_aggregator && (
                  <div className="space-y-2">
                    <Label htmlFor="actual_shipping_partner">Actual Shipping Partner *</Label>
                    <Input
                      id="actual_shipping_partner"
                      value={actualShippingPartner}
                      onChange={(e) => setActualShippingPartner(e.target.value)}
                      placeholder="Enter actual carrier name (e.g., Delhivery, Blue Dart)"
                    />
                    <p className="text-xs text-slate-500">This aggregator uses multiple carriers. Specify the actual carrier</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="auto_shipping_partner">Shipping Partner *</Label>
                  <Select
                    value={shippingPartnerId}
                    onValueChange={(value) => setShippingPartnerId(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select shipping partner for auto-assignment" />
                    </SelectTrigger>
                    <SelectContent>
                      {shippingPartners.filter(p => p.api_base_url).map((partner) => (
                        <SelectItem key={partner.id} value={partner.id}>
                          {partner.name} {partner.is_aggregator && "(Aggregator)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Only partners with API integration are shown</p>
                </div>
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowLRNDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAssignLRN} style={{backgroundColor: '#00A699'}}>
                {assignmentMode === "manual" ? "Assign LRN" : "Auto Generate LRN"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={(open) => {
        setShowStatusDialog(open);
        if (!open) setStatusUpdateMode("");
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Order Status</DialogTitle>
            <DialogDescription>
              {!statusUpdateMode ? "Choose how to update the status" : statusUpdateMode === "auto" ? "Auto-fetch status from shipping partner" : "Manually select a new status"}
            </DialogDescription>
          </DialogHeader>
          
          {!statusUpdateMode ? (
            // Mode Selection
            <div className="space-y-4">
              <div className={`grid ${selectedOrder?.shipping_partner_name?.toLowerCase() === 'delhivery' ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                <Button
                  variant="outline"
                  className="h-24 flex flex-col items-center justify-center gap-2 hover:border-[#00A699] hover:bg-[#00A699]/5"
                  onClick={() => setStatusUpdateMode("auto")}
                  disabled={!selectedOrder?.lrn || !selectedOrder?.shipping_partner_id}
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="font-semibold">Auto Fetch</span>
                  <span className="text-xs text-slate-500 text-center">Track from shipping partner</span>
                </Button>
                
                {/* Hide Manual Update for system integrated shipping partners like Delhivery */}
                {selectedOrder?.shipping_partner_name?.toLowerCase() !== 'delhivery' && (
                  <Button
                    variant="outline"
                    className="h-24 flex flex-col items-center justify-center gap-2 hover:border-[#00A699] hover:bg-[#00A699]/5"
                    onClick={() => setStatusUpdateMode("manual")}
                  >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span className="font-semibold">Manual Update</span>
                    <span className="text-xs text-slate-500 text-center">Select status manually</span>
                  </Button>
                )}
              </div>
              
              {!selectedOrder?.lrn && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  ⚠️ Auto Fetch is disabled because this order doesn't have an LRN assigned
                </p>
              )}
              
              {selectedOrder?.shipping_partner_name?.toLowerCase() === 'delhivery' && (
                <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                  ℹ️ Manual status update is not available for system integrated shipping partners
                </p>
              )}
              
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : statusUpdateMode === "auto" ? (
            // Auto Fetch Mode
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="font-medium text-blue-900">Auto Fetch Status</p>
                    <p className="text-sm text-blue-700 mt-1">
                      This will fetch the latest status from <strong>{selectedOrder?.shipping_partner_name}</strong> for LRN <strong>{selectedOrder?.lrn}</strong>
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setStatusUpdateMode("")}>
                  Back
                </Button>
                <Button onClick={handleUpdateStatus} style={{backgroundColor: '#00A699'}}>
                  Fetch Status
                </Button>
              </div>
            </div>
          ) : (
            // Manual Mode
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Status *</Label>
                <Select value={statusValue} onValueChange={setStatusValue}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ORDER_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status.replace(/_/g, ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setStatusUpdateMode("")}>
                  Back
                </Button>
                <Button onClick={handleUpdateStatus} style={{backgroundColor: '#00A699'}}>
                  Update Status
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Appointment Dialog */}
      <Dialog open={showAppointmentDialog} onOpenChange={setShowAppointmentDialog}>
        <DialogContent className="w-[80vw] sm:max-w-2xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedOrder?.appointment_date ? 'Edit Appointment' : 'Add Appointment'}</DialogTitle>
            <DialogDescription>
              {selectedOrder?.appointment_date ? 'Update the appointment date and time for this order' : 'Set appointment date and time for this order'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <AppointmentWarning />
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">How are you setting this appointment?</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant={apptMode === "partner" ? "default" : "outline"} size="sm"
                  onClick={() => setApptMode("partner")}
                  style={apptMode === "partner" ? { backgroundColor: "#00A699" } : {}}
                  data-testid="appt-mode-partner">
                  Book with Shipping Partner
                </Button>
                <Button type="button" variant={apptMode === "manual" ? "default" : "outline"} size="sm"
                  onClick={() => setApptMode("manual")}
                  style={apptMode === "manual" ? { backgroundColor: "#00A699" } : {}}
                  data-testid="appt-mode-manual">
                  Manual · Green Channel
                </Button>
              </div>
              {apptMode === "manual" && (
                <p className="text-xs text-slate-400">EdgeLMS-only — records the appointment without calling the shipping partner.</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="appointment_date">Appointment Date *</Label>
                <Input
                  id="appointment_date"
                  type="date"
                  value={appointmentDate}
                  onChange={(e) => setAppointmentDate(e.target.value)}
                  min={getMinAppointmentDate().toISOString().split('T')[0]}
                  max={getMaxAppointmentDate().toISOString().split('T')[0]}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="appointment_time_range">Time Range *</Label>
                <Select
                  value={appointmentTimeRange}
                  onValueChange={setAppointmentTimeRange}
                  disabled={!appointmentDate}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select time range" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_RANGES.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              {(() => { const req = apptMode !== "manual" && !!marketplaces.find((m) => m.id === selectedOrder?.b2b_marketplace_id)?.appointment_id_required; return (<>
              <Label htmlFor="appt_delhivery_id">Delhivery Appointment ID{req ? " *" : " (optional)"}</Label>
              <Input id="appt_delhivery_id" value={apptDelhiveryId} onChange={(e) => setApptDelhiveryId(e.target.value)} placeholder="Enter Delhivery appointment ID" />
              {req && <p className="text-xs text-red-500">Required to book the appointment for this marketplace.</p>}
              </>); })()}
            </div>
            {selectedOrder && !hasCompleteRef(selectedOrder) && (
              <>
                <p className="text-xs text-red-500">A reference/PO number (with its date and PO expiry) is required to set an appointment.</p>
                <div className="space-y-2">
                  <Label htmlFor="appt_ref_number">Reference / PO Number *</Label>
                  <Input id="appt_ref_number" value={apptRefNumber} onChange={(e) => setApptRefNumber(e.target.value)} placeholder="PO/Reference number" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="appt_ref_date">Reference Date *</Label>
                  <Input id="appt_ref_date" type="date" value={apptRefDate} onChange={(e) => setApptRefDate(e.target.value)} />
                </div>
              </>
            )}
            {selectedOrder && (!hasCompleteRef(selectedOrder) || !selectedOrder.po_expiry_date) && (
              <div className="space-y-2">
                <Label htmlFor="appt_po_expiry">PO Expiry Date *</Label>
                <Input id="appt_po_expiry" type="date" value={apptPoExpiry} onChange={(e) => setApptPoExpiry(e.target.value)} />
              </div>
            )}
            {apptMode === "manual" && (
              <>
                <div className="space-y-2">
                  <Label>Confirmation method *</Label>
                  <Select value={manualMethod} onValueChange={setManualMethod}>
                    <SelectTrigger><SelectValue placeholder="How was it confirmed with the partner?" /></SelectTrigger>
                    <SelectContent>
                      {MANUAL_METHODS.map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                {manualMethod === "other" && (
                  <div className="space-y-2">
                    <Label>Describe the method *</Label>
                    <Input value={manualMethodOther} onChange={(e) => setManualMethodOther(e.target.value)} placeholder="e.g. Confirmed over a call" />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Comment (optional)</Label>
                  <Textarea value={manualComment} onChange={(e) => setManualComment(e.target.value)} placeholder="Any note about this manual update" rows={2} />
                </div>
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAppointmentDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateAppointment} style={{backgroundColor: '#00A699'}} data-testid="appt-submit">
                {apptMode === "manual"
                  ? "Save (EdgeLMS only)"
                  : (selectedOrder?.appointment_date ? 'Update Appointment' : 'Add Appointment')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminOrders;
